import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { c } from "react-compiler-runtime";
import { formatError } from "@graphiql/toolkit";
import { useRef, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { useGraphiQLActions, useGraphiQL } from "../provider.js";
import { ImagePreview } from "../image-preview.js";
import { ResponseHeader } from "./response-header/index.js";
import { ResponseTableView } from "./response-table-view/index.js";
import { ResponseTreeView } from "./response-tree-view/index.js";
import { Spinner } from "../spinner/index.js";
import { getOrCreateModel, createEditor, onEditorContainerKeyDown } from "../../utility/create-editor.js";
import { clsx } from "clsx";
import { URI_NAME, KEY_BINDINGS } from "../../constants.js";
import { useMonaco } from "../../stores/monaco.js";
import { pick } from "../../utility/pick.js";
import { cleanupDisposables } from "../../utility/cleanup-disposables.js";
import { Range } from "monaco-editor/esm/vs/editor/common/core/range.js";
const ResponseEditor = (t0) => {
  const $ = c(46);
  const {
    responseTooltip: ResponseTooltip,
    ...props
  } = t0;
  const {
    setEditor,
    run,
    setResponseView,
    dismissTransportUpgradeBanner
  } = useGraphiQLActions();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = pick("fetchError", "validationErrors", "responseEditor", "uriInstanceId", "isFetching", "lastResponse", "responseView", "transport", "transportUpgradeBannerDismissed");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    fetchError,
    validationErrors,
    responseEditor,
    uriInstanceId,
    isFetching,
    lastResponse,
    responseView,
    transport,
    transportUpgradeBannerDismissed
  } = useGraphiQL(t1);
  const ref = useRef(null);
  let t2;
  if ($[1] !== responseEditor) {
    t2 = async function handleCopy2() {
      const value = responseEditor == null ? void 0 : responseEditor.getValue();
      if (value) {
        await navigator.clipboard.writeText(value);
      }
    };
    $[1] = responseEditor;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleCopy = t2;
  let t3;
  if ($[3] !== setResponseView) {
    t3 = function handleViewChange2(view) {
      setResponseView(view);
    };
    $[3] = setResponseView;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleViewChange = t3;
  const monaco = useMonaco(_temp);
  let t4;
  let t5;
  if ($[5] !== fetchError || $[6] !== responseEditor || $[7] !== validationErrors) {
    t4 = () => {
      if (fetchError) {
        responseEditor == null ? void 0 : responseEditor.setValue(fetchError);
      }
      if (validationErrors.length) {
        responseEditor == null ? void 0 : responseEditor.setValue(formatError(validationErrors));
      }
    };
    t5 = [responseEditor, fetchError, validationErrors];
    $[5] = fetchError;
    $[6] = responseEditor;
    $[7] = validationErrors;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] !== ResponseTooltip || $[11] !== monaco || $[12] !== run || $[13] !== setEditor || $[14] !== uriInstanceId) {
    t6 = () => {
      if (!monaco) {
        return;
      }
      const model = getOrCreateModel({
        uri: `${uriInstanceId}${URI_NAME.response}`,
        value: ""
      });
      const editor = createEditor(ref, {
        model,
        readOnly: true,
        lineNumbers: "off",
        wordWrap: "on",
        contextmenu: false
      });
      setEditor({
        responseEditor: editor
      });
      let lastRoot;
      let timerId;
      const provideHover = ($model, position) => {
        const sameModel = $model.uri === model.uri;
        if (!sameModel) {
          return null;
        }
        const wordAtPosition = $model.getWordAtPosition(position);
        if (!(wordAtPosition == null ? void 0 : wordAtPosition.word.startsWith("/"))) {
          return null;
        }
        const shouldRender = ImagePreview.shouldRender(wordAtPosition.word);
        if (!shouldRender) {
          return null;
        }
        const hoverId = `hover-${position.lineNumber}-${position.column}`;
        if (timerId) {
          clearTimeout(timerId);
        }
        timerId = setTimeout(() => {
          const el = document.querySelector(`[data-id="${hoverId}"]`);
          if (!el) {
            return;
          }
          lastRoot == null ? void 0 : lastRoot.unmount();
          lastRoot = createRoot(el);
          lastRoot.render(/* @__PURE__ */ jsxs(Fragment, { children: [
            ResponseTooltip && /* @__PURE__ */ jsx(ResponseTooltip, { position, word: wordAtPosition }),
            /* @__PURE__ */ jsx(ImagePreview, { path: wordAtPosition.word })
          ] }));
        }, 500);
        return {
          range: new Range(position.lineNumber, wordAtPosition.startColumn, position.lineNumber, wordAtPosition.endColumn),
          contents: [{
            value: `<div data-id="${hoverId}">Loading...</div>`,
            supportHtml: true
          }]
        };
      };
      const languageId = model.getLanguageId();
      const disposables = [monaco.languages.registerHoverProvider(languageId, {
        provideHover
      }), editor.addAction({
        ...KEY_BINDINGS.runQuery,
        run
      }), editor, model];
      return cleanupDisposables(disposables);
    };
    $[10] = ResponseTooltip;
    $[11] = monaco;
    $[12] = run;
    $[13] = setEditor;
    $[14] = uriInstanceId;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== monaco) {
    t7 = [monaco];
    $[16] = monaco;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useEffect(t6, t7);
  const isFetcherPath = !transport;
  const showUpgradeBanner = isFetcherPath && !transportUpgradeBannerDismissed;
  const showHeader = !isFetcherPath || showUpgradeBanner;
  const t8 = clsx("graphiql-response-pane", props.className);
  let t9;
  if ($[18] !== dismissTransportUpgradeBanner || $[19] !== handleCopy || $[20] !== handleViewChange || $[21] !== lastResponse || $[22] !== responseView || $[23] !== showHeader || $[24] !== showUpgradeBanner) {
    t9 = showHeader && /* @__PURE__ */ jsx(ResponseHeader, { status: lastResponse == null ? void 0 : lastResponse.status, statusText: lastResponse == null ? void 0 : lastResponse.statusText, timeMs: (lastResponse == null ? void 0 : lastResponse.timing.totalMs) !== void 0 ? Math.round(lastResponse.timing.totalMs) : void 0, sizeBytes: lastResponse == null ? void 0 : lastResponse.size.response, upgradeNotice: showUpgradeBanner ? {
      href: "https://github.com/graphql/graphiql/blob/main/docs/migration/graphiql-6.0.0.md",
      onDismiss: dismissTransportUpgradeBanner
    } : void 0, view: responseView, onViewChange: handleViewChange, onCopy: handleCopy });
    $[18] = dismissTransportUpgradeBanner;
    $[19] = handleCopy;
    $[20] = handleViewChange;
    $[21] = lastResponse;
    $[22] = responseView;
    $[23] = showHeader;
    $[24] = showUpgradeBanner;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== isFetching) {
    t10 = isFetching && /* @__PURE__ */ jsx(Spinner, { className: "graphiql-response-spinner-overlay" });
    $[26] = isFetching;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const t11 = responseView !== "json";
  let t12;
  if ($[28] !== t11) {
    t12 = /* @__PURE__ */ jsx("section", { ref, "aria-label": "Result Window", "aria-live": "polite", "aria-atomic": "true", tabIndex: 0, onKeyDown: onEditorContainerKeyDown, className: "result-window", hidden: t11 });
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== lastResponse || $[31] !== responseView) {
    t13 = responseView === "tree" && (lastResponse ? /* @__PURE__ */ jsx(ResponseTreeView, { data: lastResponse.body }) : /* @__PURE__ */ jsx("div", { className: "graphiql-response-empty-state", role: "status", children: /* @__PURE__ */ jsx("span", { children: "Run a query to see the tree view." }) }));
    $[30] = lastResponse;
    $[31] = responseView;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== (lastResponse == null ? void 0 : lastResponse.body) || $[34] !== responseView) {
    t14 = responseView === "table" && /* @__PURE__ */ jsx(ResponseTableView, { data: lastResponse == null ? void 0 : lastResponse.body });
    $[33] = lastResponse == null ? void 0 : lastResponse.body;
    $[34] = responseView;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t10 || $[37] !== t12 || $[38] !== t13 || $[39] !== t14) {
    t15 = /* @__PURE__ */ jsxs("div", { className: "graphiql-response-body", children: [
      t10,
      t12,
      t13,
      t14
    ] });
    $[36] = t10;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== props || $[42] !== t15 || $[43] !== t8 || $[44] !== t9) {
    t16 = /* @__PURE__ */ jsxs("div", { ...props, className: t8, children: [
      t9,
      t15
    ] });
    $[41] = props;
    $[42] = t15;
    $[43] = t8;
    $[44] = t9;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
};
function _temp(state) {
  return state.monaco;
}
export {
  ResponseEditor
};
//# sourceMappingURL=index.js.map
